import { getCookie } from './../utils/cookie'
import { readError, fetchPost as sharedFetchPost } from './api/_shared'

const csrftoken = (): string => {
    return getCookie('csrftoken') ?? ''
}

const createURL = (url: string, options) => {
    url = url + '?'
    if (options?.startFilters) url += `&start_filters=${encodeURIComponent(JSON.stringify(options.startFilters))}`
    if (options?.filters) url += `&filters=${encodeURIComponent(JSON.stringify(options.filters))}`
    if (options?.offset) url += `&offset=${options.offset}`
    if (options?.pageSize) url += `&page_size=${options.pageSize}`
    return url
}

const fetchGet = async ({ url, headers }: { url: string; headers?: any }) => {
    return fetch(url, {
        method: 'GET',
        headers: headers,
    })
}

// One implementation, shared with api/_shared.ts, so a refusal reads the same
// everywhere (see `readError`).
const fetchPost = sharedFetchPost

const BASE_URL = '/team-tools/api'

// Statistic

export type StatisticType = {
    total: number
    activated: number
    today: number
    today_activated: number
    week: number
    week_activated: number
    month: number
    month_activated: number
    beta_keys: number
    beta_requests: number
    discords: number
    twitters: number
    wallets_with_operative: number
    wallets_without_operative: number
    only_wallets_with_operative: number
    only_wallets_with_operative_and_beta: number
    only_wallets_without_operative: number
    only_wallets_without_operative_and_beta: number
    only_discord: number
    only_discord_and_beta: number
    only_twitter: number
    only_twitter_and_beta: number
    only_wallets_with_operative_and_discord: number
    only_wallets_with_operative_and_discord_and_beta: number
    only_wallets_with_operative_and_twitter: number
    only_wallets_with_operative_and_twitter_and_beta: number
    only_wallets_without_operative_and_discord: number
    only_wallets_without_operative_and_discord_and_beta: number
    only_wallets_without_operative_and_twitter: number
    only_wallets_without_operative_and_twitter_and_beta: number
    only_discord_and_twitter: number
    only_discord_and_twitter_and_beta: number
    only_wallets_with_operative_and_discord_and_twitter: number
    only_wallets_with_operative_and_discord_and_twitter_and_beta: number
    only_wallets_without_operative_and_discord_and_twitter: number
    only_wallets_without_operative_and_discord_and_twitter_and_beta: number
    nft: number
    nft_linked: number
    holders: number
    holders_linked: number
    referral_visits: number
    referral_signups: number
    guilds: {
        diamond: { total: number; registered: number }
        platinum: { total: number; registered: number }
        gold: { total: number; registered: number }
    }
}

class Statistic {
    async statistic(): Promise<StatisticType> {
        const response = await fetch(`${BASE_URL}/statistic`)
        return await response.json()
    }
}

// Team

export type TeamUser = {
    id: number
    username: string
    email: string
    last_login: string
    is_staff: boolean
    is_developer: boolean
    is_marketing: boolean
    is_executive: boolean
    // D-080: the other two ways into Team Tools.
    is_superuser: boolean
    capabilities: string[]
}

class Team {
    async members(): Promise<Array<TeamUser>> {
        const response = await fetch(`${BASE_URL}/team/members`)
        return await response.json()
    }
}

// Api inspector

export type AccessLogEntry = {
    id: number
    path: string
    request: object
    ip: string
    status: number
    response: object
    timestamp: string
}

class ApiInspector {
    async accessLog(options): Promise<{ total: number; filtered: number; entries: Array<AccessLogEntry> }> {
        const response = await fetch(createURL(`${BASE_URL}/access-log`, options))
        return await response.json()
    }

    // async login(last_id?): Promise<Array<GameAccessEntry>> {
    //     const response = await fetch(`${BASE_URL}/api-inspector/login${last_id ? '?last_id=' + last_id : ''}`)
    //     return await response.json()
    // }

    // async signup(last_id?): Promise<Array<GameAccessEntry>> {
    //     const response = await fetch(`${BASE_URL}/api-inspector/signup${last_id ? '?last_id=' + last_id : ''}`)
    //     return await response.json()
    // }

    // async join(last_id?): Promise<Array<GameAccessEntry>> {
    //     const response = await fetch(`${BASE_URL}/api-inspector/join${last_id ? '?last_id=' + last_id : ''}`)
    //     return await response.json()
    // }

    // async match(last_id?): Promise<Array<GameAccessEntry>> {
    //     const response = await fetch(`${BASE_URL}/api-inspector/match${last_id ? '?last_id=' + last_id : ''}`)
    //     return await response.json()
    // }
}

// Closed BETA

export type ClosedBetaKey = {
    id: number
    key: string
    type: number
    created_at: string
    created_by_id: number
    created_by_username: string
    comment: string
    max_usage: number
    usage: number
    auto_accept: boolean
}

export type ClosedBetaRequest = {
    id: number
    user_id: number
    user_username: string
    type: number
    created_at: string
    status: number
    status_changed_by_id: number
    status_changed_at: string
    comment: string
    discord: string
    twitter: string
    wallets_count: number
    apes_count: number
    droids_count: number
    key: string
}

export type ClosedBetaUser = {
    id: number
    user_id: number
    user_username: string
    key_id: number
    key_value: string
    source: number
    accepted_by_id: number
    accepted_by_username: string
    created_at: string
    discord: string
    twitter: string
    wallets_count: number
    apes_count: number
    droids_count: number
}

class ClosedBeta {
    async keys(offset = 0, filters = {}): Promise<{ total: number; filtered: number; keys: Array<ClosedBetaKey> }> {
        const response = await fetch(
            `${BASE_URL}/closed_beta/keys?offset=${offset}&filters=${encodeURIComponent(JSON.stringify(filters))}`
        )
        return await response.json()
    }

    async generateKey(key, max_usage = null, auto_accept = false, comment = null): Promise<void> {
        const response = await fetch(`${BASE_URL}/closed_beta/keys`, {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ key, max_usage, auto_accept, comment }),
        })
        if (response.ok) return response.json()
        else throw await readError(response)
    }

    async deleteKey(id): Promise<void> {
        const response = await fetch(`${BASE_URL}/closed_beta/keys`, {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ id }),
        })
        if (response.ok) return response.json()
        else throw await readError(response)
    }

    // async requests(offset = 0, filters = {}): Promise<{total: number, filtered: number, requests: Array<ClosedBetaRequest>}> {
    //     const response = await fetch(`${BASE_URL}/closed_beta/requests?offset=${offset}&filters=${encodeURIComponent(JSON.stringify(filters))}`)
    //     return await response.json()
    // }

    async requests(query?, headers?): Promise<{ total: number; filtered: number; list: Array<User> }> {
        return fetchPost({
            url: `${BASE_URL}/closed_beta/requests`,
            headers: {
                ...headers,
            },
            body: { query },
        })
    }

    async acceptRequest(id, settings = {}, with_airdrop = false): Promise<{ id: number; key: string }> {
        const response = await fetch(`${BASE_URL}/closed_beta/requests`, {
            method: 'PUT',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ id, ...settings, with_airdrop }),
        })
        if (response.ok) return response.json()
        else throw await readError(response)
    }

    async rejectRequest(id): Promise<{ id: number; key: string }> {
        const response = await fetch(`${BASE_URL}/closed_beta/requests`, {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ id }),
        })
        if (response.ok) return response.json()
        else throw await readError(response)
    }

    // async users(offset = 0, filters = {}): Promise<{total: number, filtered: number, users: Array<ClosedBetaUser>}> {
    //     const response = await fetch(`${BASE_URL}/closed_beta/users?offset=${offset}&filters=${encodeURIComponent(JSON.stringify(filters))}`)
    //     return await response.json()
    // }

    async users(query?, headers?): Promise<{ total: number; filtered: number; list: Array<User> }> {
        return fetchPost({
            url: `${BASE_URL}/closed_beta/users`,
            headers: {
                ...headers,
            },
            body: { query },
        })
    }

    async rejectUser(id): Promise<{ id: number; key: string }> {
        const response = await fetch(`${BASE_URL}/closed_beta/users`, {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ id }),
        })
        if (response.ok) return response.json()
        else throw await readError(response)
    }
}

// Bank

export type Balance = {
    symbol: string
    amount: string
}

export type Account = {
    id: number
    username: string
    currencies: Array<Balance>
    /** The USDC balance alone. Never a sum across currencies. */
    usdc: string
}

// Only what a screen calls. Methods for /bank/transactions, /bank/airdrop/list
// and the airdrop confirm/cancel endpoints were left here after the endpoints
// were removed, with two unused components that called them: anything wired to
// them got a 404 (audit concern 34).
class Bank {
    async depositBonus(amount, comment = null): Promise<{ transaction: number }> {
        const response = await fetch(`${BASE_URL}/bank/deposit/bonus`, {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ amount, comment }),
        })
        if (response.ok) return response.json()
        // Parsing an HTML error page threw the reason away and the form showed
        // "Error: Error" (audit C5). `readError` keeps the server's message.
        throw await readError(response)
    }

    async accounts(options): Promise<{ total: number; filtered: number; accounts: Array<Account> }> {
        const response = await fetch(createURL(`${BASE_URL}/bank/accounts`, options))
        return await response.json()
    }
}

// Ban tool

export type BanLogEntry = {
    id: number
    is_active: boolean
    type: number
    reason: number
    /** The category spelled out, off `Ban.REASON_CHOICES`. */
    reason_name?: string
    banned_at: string
    /** Null when no human placed the ban: automatic cheat enforcement did. */
    banned_by: string | null
    banned_until: string
    is_permanent: boolean
    ban_comment: string
    /** `Ban.kind`: 1 pause, 2 ban. */
    kind?: number
    kind_name?: string
    type_name?: string
    duration_days?: number | null
    duration_hours?: number | null
    unbanned_by?: string | null
    unbanned_at?: string | null
}

export type BannedUser = {
    id: number
    username: string
    is_banned: boolean
    ban: Array<BanLogEntry>
    /** What `BanUserView` actually returns. `ban` above is not a field
     *  the endpoint sends, which is why the log on the ban list has
     *  always rendered empty. Named correctly here so the ban state can
     *  at least explain itself; `ban` is left alone rather than renamed,
     *  since untangling that page is its own job. */
    ban_log?: Array<BanLogEntry>
    active_ban_id?: number | null
}

export type BanRequest = {
    /** One account. Still what most callers send. */
    user_id?: number
    /** Several accounts on ONE decision -- a ring found on an address, banned
     *  with one reason instead of typed out name by name. The endpoint has
     *  understood this for a while; nothing in the browser was sending it.
     *
     *  Cannot be combined with `ip`/`ips`: addresses belong to the account they
     *  were seen on, and the server refuses the pair rather than attributing
     *  one account's addresses to all of them. */
    user_ids?: number[]
    /** Past the server's group ceiling the caller has to say it meant it. The
     *  ceiling itself is the server's, so this is sent only after a refusal
     *  rather than guessed at here. */
    confirm_large?: boolean
    /** Which of the two states to put the account in.
     *
     *  1 = Pause -- out of the game while somebody looks at it, expected back.
     *  2 = Ban   -- they cheated, permanently.
     *
     *  Both are enforced identically; the difference is what the player is told
     *  and what staff mean by it. Omitted means Ban, which is what every caller
     *  written before this field meant. */
    kind?: 1 | 2
    reason: number
    is_permanent: boolean
    banned_until: string
    comment: string
    /** Bans the address as well as the account. Optional: absent means an account ban. */
    ip?: string
    /**
     * Several addresses at once -- "ban this account and everywhere it has been seen".
     *
     * One row is written per address, each carrying the user, so the account is stopped
     * too. Prefer this over `ip`, which the server still accepts only so a browser holding
     * a cached bundle through a deploy keeps working.
     */
    ips?: string[]
    /** Machine ids to ban as well, one row each. Like addresses, one account at a time. */
    hwids?: string[]
    /** Shown to the player instead of the standard wording. Blank falls back to it. */
    player_message?: string
}

/** What `ban-tool/user` reports back about what it just did. */
export type BanResponse = {
    /** The first row written. Kept for callers that only ever ban one thing. */
    ban_id: number
    type: number
    ip: string | null
    /** Every row written, one per address, each individually liftable. */
    ban_ids: number[]
    /** The addresses that were banned. */
    ips: string[]
    /** The accounts that were banned. */
    user_ids: number[]
    /** Addresses the server refused, and why -- a private range, a reserved
     *  block, something malformed. Named rather than swallowed, so a partial
     *  success cannot read as a complete one. */
    skipped: Array<{ ip: string; why: string }>
}

class BanTool {
    async user(username: string): Promise<BannedUser> {
        const response = await fetch(`${BASE_URL}/ban-tool/user?username=${username}`)
        if (response.ok) return await response.json()
        // A refusal is not a server fault. Every non-404 used to read "Api
        // server error", so a staff member without the ban grant was told the
        // server was broken (audit ACCL-70). Pass on the server's own reason
        // when it gives one; a bare refusal still names the ban tool.
        if (response.status === 404) throw new Error('User not found')
        const err = await readError(response)
        const reason = typeof err.error === 'string' ? err.error : JSON.stringify(err.error)
        if (/^(Refused|Not signed in) \(HTTP \d+\)$|PermissionDenied|Unauthorized/.test(reason))
            throw new Error('You are not allowed to use the ban tool')
        throw new Error(reason)
    }

    /** Returns the endpoint's own report: which bans were written, and which
     *  addresses it refused and why. Discarding that and returning `true` meant
     *  a caller banning several addresses could not tell the caller which of
     *  them did not take, so a partial success read as a complete one. */
    async ban(request: BanRequest): Promise<BanResponse> {
        const response = await fetch(`${BASE_URL}/ban-tool/user`, {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify(request),
        })

        if (!response.ok) {
            // The endpoint's own refusals are short plain text; a permission
            // refusal is now a JSON 403 with the reason (it used to be Django's
            // HTML error page, printed whole: audit concern 37). `readError`
            // turns either into one sentence and never dumps an HTML page.
            throw new Error((await readError(response)).error)
        }
        return await response.json()
    }

    /**
     * Lift a ban. The row is kept and stamped with who lifted it and why, rather than deleted,
     * so the history on the account stays readable.
     */
    async unban(ban_id: number, comment?: string): Promise<boolean> {
        const response = await fetch(`${BASE_URL}/ban-tool/user`, {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({ ban_id, comment: comment ?? null }),
        })

        if (!response.ok) throw new Error((await readError(response)).error)
        return true
    }
}

// Accounts

export type User = {
    ape_holder: boolean
    apes: number
    applied_for_beta: boolean
    beta_key: string
    beta_tester: boolean
    date_joined: string
    discord: string
    discord_connected: boolean
    droid_holder: boolean
    droids: number
    email: string
    id: number
    is_active: boolean
    referral_signups: number
    referral_visits: number
    twitter: string
    twitter_connected: boolean
    usdc: string
    username: string
    wallet_connected: boolean
    wallets: number
    referrals?: Array<any>
    wallet_list: Array<{ pubkey: string; name: string }>
}

export type UserRoleAction = {
    user_id: number
    role_name: string
    result: string
}

class Accounts {
    async users(query?, headers?): Promise<{ total: number; filtered: number; list: Array<User> }> {
        return fetchPost({
            url: `${BASE_URL}/accounts/users`,
            headers: {
                ...headers,
            },
            body: { query },
        })
    }

    async user(query, headers?): Promise<User> {
        return fetchPost({
            url: `${BASE_URL}/accounts/user`,
            headers: {
                ...headers,
            },
            body: { query },
        })
    }

    async setRole({ user_id, role_name, action }): Promise<UserRoleAction> {
        return fetchPost({
            url: `/api/account/role`,
            body: { user_id, role_name, action },
        })
    }
}

class RaceGame {
    async queueAdd({ query, queue = 'default' }): Promise<boolean> {
        const response = await fetch('/api/race/queue', {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({queue, query}),
        })

        return response.ok
    }

    async queueDelete({ query, queue = 'default' }): Promise<boolean> {
        const response = await fetch('/api/race/queue', {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrftoken(),
            },
            body: JSON.stringify({queue, query}),
        })

        return response.ok
    }
}

export default {
    statistic: new Statistic(),
    team: new Team(),
    api_inspector: new ApiInspector(),
    closed_beta: new ClosedBeta(),
    bank: new Bank(),
    ban_tool: new BanTool(),
    accounts: new Accounts(),
    racegame: new RaceGame(),
}
